import { Box, Chip, Paper, Typography, useTheme } from "@mui/material";
import { IMultiSectionUnitConfiguration } from "../../../Models/IUnitConfiguration";

/**
 * Props for the MultiSectionTiles component
 */
interface MultiSectionTilesProps {
    /** Array of section configurations to display */
    sections: IMultiSectionUnitConfiguration[];
}

/**
 * Props for the InfoItem component
 */
interface InfoItemProps {
    /** Label text */
    label: string;
    /** Value text (optional) */
    value?: string;
}

/**
 * MultiSectionTiles component
 * 
 * Displays information about heat exchanger sections in a tile layout
 */
export const MultiSectionTiles: React.FC<MultiSectionTilesProps> = ({ sections }) => {
    const theme = useTheme();

    // Get the first section for common information
    const firstSection = sections[0];
    
    // Sort sections in descending order based on sectionNumber (create a new array to avoid mutating props)
    const sortedSections = [...sections].sort((a, b) => 
        (b.sectionNumber ?? 0) - (a.sectionNumber ?? 0)
    );

    // Styles organized by component
    const styles = {
        // Container styles
        container: {
            m: 2.5,
            p: 1,
            bgcolor: 'background.paper',
        },
        
        // Info section styles
        infoContainer: {
            p: 1,
            mb: 1.25,
        },
        infoRow: {
            display: 'flex',
            justifyContent: 'center',
            gap: 2.5,
            mt: 0.625,
        },
        infoLabel: {
            fontWeight: 'bold',
            color: theme.palette.text.primary,
        },
        operationBadge: {
            mt: 1.25,
            display: 'flex',
            justifyContent: 'center',
        },
        
        // Tiles section styles
        tilesContainer: {
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
        sectionTile: {
            borderTop: `solid 5px ${theme.palette.primary.main}`,
            m: 0.625,
            p: 1.25,
            minWidth: 370,
            '&:hover': {
                boxShadow: 3,
                cursor: 'pointer',
            },
        },
        sectionHeader: {
            display: 'flex',
            gap: 0.625,
            borderBottom: `solid 1px ${theme.palette.primary.main}`,
            pb: 0.5,
            mb: 1,
        },
        sectionContent: {
            display: 'flex',
            gap: 1.875,
        },
        sectionColumn: {
            fontSize: '0.875rem',
            p: 0,
        },
        calculationBadge: {
            ml: 1.25,
            mt: 0.5,
        }
    };

    return (
        <Box sx={styles.container}>
            {/* General unit information */}
            <SectionInfo 
                section={firstSection} 
                styles={styles} 
            />
            
            {/* Section tiles */}
            <Box sx={styles.tilesContainer}>
                {sortedSections.map((section) => (
                    <SectionTile 
                        key={`section-${section.sectionNumber}`} 
                        section={section} 
                        styles={styles}
                    />
                ))}
            </Box>
        </Box>
    );
};

/**
 * InfoItem component for displaying label-value pairs
 */
const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
    const theme = useTheme();
    return (
        <Box>
            <Typography variant="body2">{label}</Typography>
            <Paper sx={{ pl: 1, pr:1, backgroundColor: theme.palette.background.paper}}>
            <Typography sx={{ fontWeight: 'bold'}}>{value || ''}</Typography>
            </Paper>
        </Box>
    );
};

/**
 * SectionInfo component for displaying general unit information
 */
const SectionInfo: React.FC<{ 
    section: IMultiSectionUnitConfiguration; 
    styles: Record<string, any>;
}> = ({ section, styles }) => (
    <Box sx={styles.infoContainer}>
        <Box sx={styles.infoRow}>
            <InfoItem label="Industry" value={section.industry} />
            <InfoItem label="SubIndustry" value={section.subIndustry} />
            <InfoItem label="Application" value={section.application} />
            <InfoItem label="SubApplication" value={section.subApplication} />
        </Box>
        
        <Box sx={styles.infoRow}>
            <InfoItem label="ProcessStage" value={section.processStage} />
            <InfoItem label="ProductLine" value={section.productLine} />
            <InfoItem label="AlfaLavalPosition" value={section.alfaLavalPosition} />
            <InfoItem label="FlowType" value={section.flowType} />
        </Box>

        {(section.unitOperationTargetName || section.unitOperationTargetValueUnit) && (
            <Box sx={styles.operationBadge}>
                <Chip 
                    color="success" 
                    variant="outlined" 
                    label={`Operation target: ${section.unitOperationTargetName || ''} ${section.unitOperationTargetValueUnit || ''}`} 
                />
            </Box>
        )}
    </Box>
);

/**
 * PropertyList component for displaying property lists (inputs, outputs, etc.)
 */
const PropertyList: React.FC<{
    title: string;
    properties?: string[];
    styles: Record<string, any>;
}> = ({ title, properties = [], styles }) => (
    <Box sx={styles.sectionColumn}>
        <Typography variant="subtitle2">{title}</Typography>
        {properties.map((property, index) => (
            <Typography key={`${title.toLowerCase()}-${index}`} variant="body2">
                {property}
            </Typography>
        ))}
    </Box>
);

/**
 * SectionTile component for displaying a single section
 */
const SectionTile: React.FC<{
    section: IMultiSectionUnitConfiguration;
    styles: Record<string, any>;
}> = ({ section, styles }) => {
    const theme = useTheme();
    
    return (
        <Box>
            <Paper sx={styles.sectionTile} elevation={1}>
                <Box sx={styles.sectionHeader}>
                    <Box flexGrow={1}>
                        <Typography variant="subtitle1">
                            Section {section.sectionNumber || ''}
                        </Typography>
                    </Box>
                    
                    {section.hotMediaType && (
                        <Box sx={{ p: 0.625 }}>
                            <Chip 
                                label={section.hotMediaType}
                                sx={{ bgcolor: theme.palette.error.main, color: 'white' }}
                                size="small"
                            />
                        </Box>
                    )}
                    
                    {section.coldMediaType && (
                        <Box sx={{ p: 0.625 }}>
                            <Chip 
                                label={section.coldMediaType} 
                                sx={{ bgcolor: theme.palette.primary.main, color: 'white' }}
                                size="small"
                            />
                        </Box>
                    )}
                </Box>
                
                <Box sx={styles.sectionContent}>
                    <PropertyList 
                        title="Inputs" 
                        properties={section.inputs}
                        styles={styles}
                    />
                    
                    <PropertyList 
                        title="Outputs" 
                        properties={section.outputs}
                        styles={styles}
                    />
                    
                    <PropertyList 
                        title="Not used" 
                        properties={section.nonUsedProperties}
                        styles={styles}
                    />
                </Box>
            </Paper>
            
            {section.calculationType && (
                <Box sx={styles.calculationBadge}>
                    <Chip 
                        label={`Calculation type: ${section.calculationType}`} 
                        variant="outlined"
                        size="small"
                    />
                </Box>
            )}
        </Box>
    );
}; 